import { create, show, useModal } from "@ebay/nice-modal-react";
import { useForm } from "react-hook-form";

import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import ModalSimple from "ds/components/ModalSimple";
import Typography from "ds/components/Typography";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import {
  GET_USER_GUIDE_CERTIFICATION_LIST,
  USER_GUIDE_CERTIFICATION_UPDATE,
} from "shared/UserGuides/gql";
import { stringIsRequired } from "utils/formValidators";

const useUpdateUserGuideCertification = createDefaultMutationHook(USER_GUIDE_CERTIFICATION_UPDATE);

type EditCertificateModalProps = {
  certificateId: string;
  currentDisplayName: string;
};

type NameFormValues = {
  fullName: string;
};

const EditCertificateModal = create(function EditCertificateModal({
  certificateId,
  currentDisplayName,
}: EditCertificateModalProps) {
  const modal = useModal();
  const [updateCertificate, { loading }] = useUpdateUserGuideCertification({
    refetchQueries: [GET_USER_GUIDE_CERTIFICATION_LIST],
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NameFormValues>({ defaultValues: { fullName: currentDisplayName } });

  const onSubmit = (values: NameFormValues) => {
    updateCertificate(
      {
        id: certificateId,
        displayName: values.fullName.trim(),
      },
      (data) => {
        if (data?.userGuideCertificationUpdate) {
          modal.hide();
        }
      }
    );
  };

  return (
    <ModalSimple isDismissable={!loading}>
      <ModalHeader title="Edit your name" />
      <ModalContent>
        <Typography variant="p-body3" tag="p">
          Update the full name shown on your certificate.
        </Typography>
        <FormField label="Full name" error={errors.fullName?.message}>
          {({ ariaInputProps }) => (
            <Input
              placeholder="Your full name"
              error={!!errors.fullName}
              autoFocus
              maxLength={50}
              {...register("fullName", {
                validate: stringIsRequired("Full name is required."),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>
      </ModalContent>
      <ModalFooter>
        <ModalCancelButton onClick={modal.hide} />
        <Button variant="primary" loading={loading} onPress={() => handleSubmit(onSubmit)()}>
          Save
        </Button>
      </ModalFooter>
    </ModalSimple>
  );
});

export const showEditCertificateModal = (props: EditCertificateModalProps) =>
  show(EditCertificateModal, props);
